import { useEffect, useMemo, useState } from 'react';
import type { TCTemplateRecord } from 'src/api/generated';
import type { MappingValue } from 'src/modules/automations/components/molecules/create-trigger-modal/mapping-row';
import type {
  GuidedHandoffTemplateKind,
  GuidedHandoffValues,
  QuickAutomationContext,
} from 'src/modules/automations/types/quick-automation.type';
import {
  areGuidedHandoffValuesValid,
  buildGuidedHandoffMapping,
  getExcludedContentDefault,
  suggestEnabledHandoffStatusIds,
} from 'src/modules/automations/utils/templates/handoff-template.util';
import { buildDefaultMapping } from 'src/modules/automations/utils/templates/template-mapping.util';
import { useGuidedHandoffSetup } from './use-guided-handoff-setup';

/**
 * Internal state that records which context supplied the current defaults.
 */
interface GuidedHandoffFormState {
  /** Template/funnel/status identity used to initialize the form. */
  initializationKey: string;
  /** User-editable values for the guided handoff mapping. */
  values: GuidedHandoffValues;
}

const EMPTY_VALUES: GuidedHandoffValues = {
  funnelId: 0,
  destinationStatusId: null,
  enabledStatusIds: [],
  employeeId: null,
  excludedMessageContent: '',
};

/**
 * Coordinates data loading, defaults, validation, and mapping overrides for a
 * guided handoff template.
 *
 * @param template - Full selected template detail.
 * @param context - Current Communications funnel and status.
 * @param kind - Validated handoff variant.
 * @param baseMapping - Decoded canonical mapping from Templates App.
 * @param enabled - Whether the guided experience is open and queryable.
 * @returns Guided form state and a complete mapping for workflow creation.
 */
export const useGuidedHandoffConfiguration = ({
  template,
  context,
  kind,
  baseMapping,
  enabled,
}: {
  template: TCTemplateRecord | null;
  context?: QuickAutomationContext;
  kind: GuidedHandoffTemplateKind | null;
  baseMapping: Record<string, MappingValue>;
  enabled: boolean;
}) => {
  const setup = useGuidedHandoffSetup(context?.funnelId, enabled);
  const [formState, setFormState] = useState<GuidedHandoffFormState>({
    initializationKey: '',
    values: EMPTY_VALUES,
  });

  const initializationKey =
    enabled && template && context && kind
      ? `${template.templateId ?? template.key}:${context.funnelId ?? 'missing'}:${context.statusId}:${kind}`
      : '';

  useEffect(() => {
    if (
      !initializationKey ||
      setup.loading ||
      setup.error ||
      !context?.funnelId ||
      !kind ||
      formState.initializationKey === initializationKey
    ) {
      return;
    }

    const canonicalMapping = buildDefaultMapping(template);
    const destinationStatusId = context.statusId;
    setFormState({
      initializationKey,
      values: {
        funnelId: context.funnelId,
        destinationStatusId,
        enabledStatusIds: suggestEnabledHandoffStatusIds(
          setup.statuses,
          destinationStatusId
        ),
        employeeId:
          setup.employees.length === 1 ? setup.employees[0].employeeId : null,
        excludedMessageContent:
          kind === 'except-content'
            ? getExcludedContentDefault(canonicalMapping)
            : '',
      },
    });
  }, [
    context,
    formState.initializationKey,
    initializationKey,
    kind,
    setup.employees,
    setup.error,
    setup.loading,
    setup.statuses,
    template,
  ]);

  const values = formState.values;
  const initialized =
    initializationKey.length > 0 &&
    formState.initializationKey === initializationKey;
  const mapping = useMemo(
    () =>
      kind
        ? buildGuidedHandoffMapping(baseMapping, values, setup.employees, kind)
        : baseMapping,
    [baseMapping, kind, setup.employees, values]
  );
  const valid =
    initialized &&
    !setup.loading &&
    !setup.error &&
    kind !== null &&
    areGuidedHandoffValuesValid(values, setup.statuses, setup.employees, kind);

  const setDestinationStatusId = (destinationStatusId: number | null) => {
    setFormState((current) => ({
      ...current,
      values: {
        ...current.values,
        destinationStatusId,
        enabledStatusIds: current.values.enabledStatusIds.filter(
          (statusId) => statusId !== destinationStatusId
        ),
      },
    }));
  };

  const setEnabledStatusIds = (enabledStatusIds: number[]) => {
    setFormState((current) => ({
      ...current,
      values: {
        ...current.values,
        enabledStatusIds: Array.from(new Set(enabledStatusIds)).filter(
          (statusId) => statusId !== current.values.destinationStatusId
        ),
      },
    }));
  };

  const setEmployeeId = (employeeId: string | null) => {
    setFormState((current) => ({
      ...current,
      values: {
        ...current.values,
        employeeId,
      },
    }));
  };

  const setExcludedMessageContent = (excludedMessageContent: string) => {
    setFormState((current) => ({
      ...current,
      values: {
        ...current.values,
        excludedMessageContent,
      },
    }));
  };

  return {
    ...setup,
    values,
    mapping,
    initialized,
    valid,
    setDestinationStatusId,
    setEnabledStatusIds,
    setEmployeeId,
    setExcludedMessageContent,
  };
};
